import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useAuth } from "../../Context/UnifiedAuthContext";
import { GoogleMap, useLoadScript, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import React from 'react';

// --- Enhanced Tour steps with clearer instructions (removed #tour-map-area step) ---
const TOUR_STEPS = [
    {
        selector: "#add-location-btn",
        message: "Start by adding a new favorite location. Click here to begin.",
        placement: "right"
    },
    {
        selector: "#help-btn",
        message: "Need help? Click here for instructions or to restart this tour anytime.",
        placement: "right"
    },
    {
        selector: "#zone-counter",
        message: "Zones are hotspots with higher listing potential. Higher zones = better chances of finding properties. If you see 0 zones, try adjusting your location to a different area.",
        placement: "right"
    },
    {
        selector: "#tour-btn",
        message: "You can restart this tour anytime by clicking this button.",
        placement: "right"
    }
];

const libraries = ["places", "geometry"];

const LocationMarker = memo(({ position, draggable, onClick, onDragEnd, onDragStart, isTemp }) => {
    const iconColor = isTemp ? "#4285F4" : "#10c9c0";

    // Only create the marker icon if google maps is loaded
    const markerIcon = typeof window !== 'undefined' && window.google && window.google.maps ? {
        path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
        fillColor: iconColor,
        fillOpacity: 1,
        strokeColor: "green",
        strokeWeight: 1,
        scale: 2.2,
        anchor: new window.google.maps.Point(12, 22)
    } : null;

    return (
        <Marker
            position={position}
            draggable={draggable}
            onClick={onClick}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            icon={markerIcon}
            animation={isTemp && window.google && window.google.maps ? window.google.maps.Animation.DROP : null}
        />
    );
});
LocationMarker.displayName = 'LocationMarker';

const FavoriteLocationsPage = () => {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const mapRef = useRef(null);

    // Base map options without position-specific options
    const baseMapOptions = {
        zoomControl: true,
        fullscreenControl: true, // Add this line to enable fullscreen
        streetViewControl: false,
        mapTypeControl: false,
        gestureHandling: "cooperative",
        clickableIcons: false,
        styles: [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
            { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
        ]
    };

    // State
    const [mapOptions, setMapOptions] = useState(baseMapOptions);
    const [activeToasts, setActiveToasts] = useState(new Map()); // Track active toasts
    const toastTimersRef = useRef(new Map()); // Track toast timers

    const [favoriteLocations, setFavoriteLocations] = useState([]);
    const [activeInfoWindow, setActiveInfoWindow] = useState(null);
    const [isAddingMode, setIsAddingMode] = useState(false);
    const [tempMarker, setTempMarker] = useState(null);
    const [selectedRadius, setSelectedRadius] = useState(5);
    const [municipalityCount, setMunicipalityCount] = useState(0);
    const [mapCenter, setMapCenter] = useState({ lat: 35.6762, lng: 139.6503 });
    const [mapZoom, setMapZoom] = useState(8);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingUpdate, setPendingUpdate] = useState(null);
    const [showHelp, setShowHelp] = useState(true);
    const [showTour, setShowTour] = useState(false);
    const [tourStep, setTourStep] = useState(0);
    const [markerBoxExpanded, setMarkerBoxExpanded] = useState(true); // Default to expanded
    const [zones, setZones] = useState(0);
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipTimeoutRef = useRef(null);
    const tourAnimationSpeedRef = useRef(null); // Reference for controlling tour animation speed

    // After maps is loaded, update the options with position-specific settings
    const onMapLoad = useCallback((map) => {
        mapRef.current = map;

        // Now that Google Maps is loaded, we can safely set the position options
        if (window.google && window.google.maps) {
            setMapOptions({
                ...baseMapOptions,
                zoomControlOptions: {
                    position: window.google.maps.ControlPosition.RIGHT_BOTTOM
                },
                fullscreenControlOptions: {
                    position: window.google.maps.ControlPosition.RIGHT_TOP
                }
            });
        }

        // Make sure InfoWindows stay on top of map
        // This ensures InfoWindow gets proper z-index
        const iwOptions = { maxWidth: 300, pixelOffset: new window.google.maps.Size(0, -30) };
        window.google.maps.InfoWindow.prototype.opened = false;

        // Override the open method
        const openFunc = window.google.maps.InfoWindow.prototype.open;
        window.google.maps.InfoWindow.prototype.open = function () {
            this.opened = true;
            openFunc.apply(this, arguments);

            // Force InfoWindow to the front with a high z-index
            const infoWindows = document.querySelectorAll('.gm-style-iw-a');
            infoWindows.forEach(iw => {
                iw.style.zIndex = '2000';

                // Ensure parent containers also have high z-index
                let parent = iw.parentElement;
                while (parent && parent.classList && !parent.classList.contains('gm-style')) {
                    parent.style.zIndex = '2000';
                    parent = parent.parentElement;
                }
            });
        };

        // Handle bounds setting for markers
        if (favoriteLocations.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            favoriteLocations.forEach(loc => {
                bounds.extend({ lat: loc.coordinate[0], lng: loc.coordinate[1] });
            });
            map.fitBounds(bounds, 50);
        }
    }, [favoriteLocations, baseMapOptions]);

    const base_url = "https://app.akiya2.com";

    // Clean up timers on component unmount
    useEffect(() => {
        return () => {
            // Clear all timers
            toastTimersRef.current.forEach(timer => clearTimeout(timer));
            toastTimersRef.current.clear();
        };
    }, []);

    useEffect(() => {
        // Add global style fixes for Google Maps InfoWindows
        const addGlobalInfoWindowFix = () => {
            const styleTag = document.createElement('style');
            styleTag.innerHTML = `
            /* Force InfoWindows to front */
            .gm-style-iw-a, 
            .gm-style-iw-t,
            .gm-style-iw-c,
            .gm-style-iw-d,
            .gm-style-iw {
                z-index: 2000 !important;
            }
            
            /* Fix for parent elements */
            .gm-style > div,
            .gm-style-iw-a > div,
            .gm-style-iw-t > div,
            .gm-style-iw-c > div,
            .gm-style-iw-d > div {
                z-index: 2000 !important;
            }
            
            /* Prevent map from hiding modals */
            .gm-style {
                z-index: 1 !important;
            }
        `;
            document.head.appendChild(styleTag);
        };

        // Apply global InfoWindow fix
        addGlobalInfoWindowFix();
    }, []);
    // Map container styles
    const mapContainerStyle = {
        width: "100vw",
        height: "98vh",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 1, // Lower z-index for the map container
    };

    // Enhanced circle visibility
    const circleOptions = {
        fillColor: "#10c9c0",
        fillOpacity: 0.25,
        strokeColor: "#10c9c0",
        strokeOpacity: 0.85,
        strokeWeight: 2.5,
        zIndex: 50, // Set a specific z-index for circles
    };

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: "AIzaSyDhd_bCluPIx2cSHS7pGd5wVPkIRU7rpxQ",
        libraries,
    });

    useEffect(() => {
        if (user) {
            const favLocations = user.fav_coordinates_and_proximity || [];
            const locationsWithIds = favLocations.map(loc => ({
                ...loc,
                id: loc.id || `loc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            }));
            setFavoriteLocations(locationsWithIds);
            setMunicipalityCount(user.favourite_municipalities?.length || 0);
        }
    }, [user]);

    // Better toast notifications with fixed z-index issues
    const showToastNotification = useCallback((message, type = "info", toastKey = null, extendable = false) => {
        // If this is an extendable toast and it already exists, extend its duration
        if (extendable && toastKey && activeToasts.has(toastKey)) {
            const existingToast = activeToasts.get(toastKey);
            const currentDuration = existingToast.duration || 4000;
            const newDuration = Math.min(currentDuration + 3000, 40000); // Max 40 seconds

            // Clear existing timer
            if (toastTimersRef.current.has(toastKey)) {
                clearTimeout(toastTimersRef.current.get(toastKey));
            }

            // Update the toast duration
            setActiveToasts(prev => new Map(prev.set(toastKey, {
                ...existingToast,
                duration: newDuration,
                extendedCount: (existingToast.extendedCount || 0) + 1
            })));

            // Set new timer to remove from tracking
            const newTimer = setTimeout(() => {
                setActiveToasts(prev => {
                    const updated = new Map(prev);
                    updated.delete(toastKey);
                    return updated;
                });
                toastTimersRef.current.delete(toastKey);
            }, newDuration);

            toastTimersRef.current.set(toastKey, newTimer);

            // Update the existing toast with new duration
            toast.update(existingToast.toastId, {
                autoClose: newDuration,
                render: `${message} (extended ${existingToast.extendedCount + 1}x)`
            });

            return;
        }

        // Create new toast
        const toastId = toast[type](extendable && toastKey ? `${message}` : message, {
            position: "top-right",
            autoClose: extendable ? 7000 : 4000, // Longer initial duration for extendable toasts
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            style: {
                zIndex: 10001,
                position: 'relative',
                background: type === 'info' ? '#e3f2fd' :
                    type === 'success' ? '#e8f5e9' :
                        type === 'warning' ? '#fff8e1' : '#ffebee',
                color: type === 'info' ? '#0277bd' :
                    type === 'success' ? '#2e7d32' :
                        type === 'warning' ? '#ff8f00' : '#c62828',
                fontWeight: 500,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            },
            toastClassName: "high-z-toast",
            bodyClassName: "high-z-toast-body",
            containerId: "toast-container-high-z",
            onClose: () => {
                // Clean up when toast is closed
                if (toastKey) {
                    setActiveToasts(prev => {
                        const updated = new Map(prev);
                        updated.delete(toastKey);
                        return updated;
                    });
                    if (toastTimersRef.current.has(toastKey)) {
                        clearTimeout(toastTimersRef.current.get(toastKey));
                        toastTimersRef.current.delete(toastKey);
                    }
                }
            }
        });

        // Track the toast if it's extendable
        if (extendable && toastKey) {
            const initialDuration = 7000;
            setActiveToasts(prev => new Map(prev.set(toastKey, {
                toastId,
                duration: initialDuration,
                message,
                type,
                extendedCount: 0
            })));

            // Set timer to clean up tracking
            const timer = setTimeout(() => {
                setActiveToasts(prev => {
                    const updated = new Map(prev);
                    updated.delete(toastKey);
                    return updated;
                });
                toastTimersRef.current.delete(toastKey);
            }, initialDuration);

            toastTimersRef.current.set(toastKey, timer);
        }

        // Force update z-index after render (keep existing logic)
        setTimeout(() => {
            const toastContainers = document.querySelectorAll('.Toastify__toast-container');
            toastContainers.forEach(container => {
                container.style.zIndex = '10001';
                container.style.position = 'fixed';
                container.style.top = '20px';
                container.style.right = '20px';
                container.style.left = 'auto';

                const toasts = container.querySelectorAll('.Toastify__toast');
                toasts.forEach(t => {
                    t.style.zIndex = '10001';
                    t.style.position = 'relative';
                });
            });
        }, 100);
    }, [activeToasts]);

    // --- Improved Tour logic with speed controls ---
    useEffect(() => {
        if (!showTour) return;

        // End tour if we've reached the end
        if (tourStep >= TOUR_STEPS.length) {
            setShowTour(false);
            setTourStep(0);
            showToastNotification("Tour completed! You're ready to start adding locations.", "success");
            return;
        }

        // Show toast for current step
        showToastNotification(TOUR_STEPS[tourStep].message);

        // Highlight the current element
        const step = TOUR_STEPS[tourStep];
        const el = document.querySelector(step.selector);
        if (el) {
            el.classList.add("tour-highlight");

            // Scroll into view if needed
            if (el.getBoundingClientRect().top < 0 ||
                el.getBoundingClientRect().bottom > window.innerHeight) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        // Remove the "ui-lock" class - we'll handle this differently
        document.body.classList.remove("tour-ui-lock");

        // Clear any existing animation speed timers
        if (tourAnimationSpeedRef.current) {
            clearTimeout(tourAnimationSpeedRef.current);
        }

        return () => {
            if (el) el.classList.remove("tour-highlight");
            if (tourAnimationSpeedRef.current) {
                clearTimeout(tourAnimationSpeedRef.current);
            }
        };
    }, [tourStep, showTour, showToastNotification]);

    // Map click handler
    const handleMapClick = useCallback((event) => {
        if (showConfirmation) {
            setShowConfirmation(false);
            setPendingUpdate(null);
            return;
        }

        if (!isAddingMode) {
            setActiveInfoWindow(null);
            return;
        }

        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        setTempMarker({
            coordinate: [lat, lng],
            proximity: selectedRadius,
            id: `temp_${Date.now()}`
        });

        setActiveInfoWindow({
            coordinate: [lat, lng],
            proximity: selectedRadius,
            id: `temp_${Date.now()}`
        });

        showToastNotification("Click and drag the marker to adjust position, or use the radius slider to change the area size.", "info");
    }, [isAddingMode, selectedRadius, showConfirmation, showToastNotification]);

    // Radius change for temp marker
    const handleTempRadiusChange = useCallback((value) => {
        setSelectedRadius(value);
        if (tempMarker) {
            const updatedTempMarker = {
                ...tempMarker,
                proximity: value
            };
            setTempMarker(updatedTempMarker);
            if (activeInfoWindow && activeInfoWindow.id === tempMarker.id) {
                setActiveInfoWindow(updatedTempMarker);
            }
        }
    }, [tempMarker, activeInfoWindow]);

    // Radius change for existing locations
    const handleLocationRadiusChange = useCallback((locationId, value) => {
        setFavoriteLocations(prevLocations => prevLocations.map(loc => {
            if (loc.id === locationId) {
                const updatedLoc = {
                    ...loc,
                    proximity: value,
                    originalProximity: loc.originalProximity || loc.proximity
                };
                if (activeInfoWindow && activeInfoWindow.id === locationId) {
                    setActiveInfoWindow(updatedLoc);
                }
                return updatedLoc;
            }
            return loc;
        }));
    }, [activeInfoWindow]);

    const goToSettings = useCallback(() => {
        // Check if user is logged in
        if (!user || !user.email) {
          toast.warning("Please log in to access alert service settings");
          return;
        }
    
        // Navigate to alert service settings
        navigate("/alert-service-settings");
      }, [user, navigate]);

    // Update other toast calls to be non-extendable (default behavior)
    // For example, in startAddingLocation:
    const startAddingLocation = useCallback(() => {
        // Check if user is logged in
        if (!user || !user.email) {
            showToastNotification("Please log in to add favorite locations", "warning");
            return;
        }

        if (favoriteLocations.length >= 3) {
            showToastNotification("Maximum of 3 favorite locations allowed", "warning");
            return;
        }

        setIsAddingMode(true);
        // Regular toast (not extendable)
        showToastNotification("Click on the map to place your marker, then drag to adjust its position.", "info");

        if (mapRef.current) {
            const center = mapRef.current.getCenter();
            setTempMarker({
                coordinate: [center.lat(), center.lng()],
                proximity: selectedRadius,
                id: `temp_${Date.now()}`
            });
            setActiveInfoWindow({
                coordinate: [center.lat(), center.lng()],
                proximity: selectedRadius,
                id: `temp_${Date.now()}`
            });
        }
    }, [favoriteLocations.length, selectedRadius, showToastNotification, user]);

    // Save the temporary marker as a new location
    const saveNewLocation = useCallback(async () => {
        if (!tempMarker) return;

        try {
            showToastNotification("Saving your location...", "info");

            const response = await axios.post(base_url + '/favorite-locations', {
                coordinate: tempMarker.coordinate,
                proximity: tempMarker.proximity
            }, {
                params: { email: user.email }
            });

            if (response.data.success) {
                const locationsWithIds = response.data.fav_coordinates_and_proximity.map(loc => ({
                    ...loc,
                    id: loc.id || `loc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                }));

                setFavoriteLocations(locationsWithIds);
                setMunicipalityCount(response.data.favourite_municipalities.length);
                updateUser({
                    ...user,
                    fav_coordinates_and_proximity: response.data.fav_coordinates_and_proximity,
                    favourite_municipalities: response.data.favourite_municipalities
                });

                showToastNotification("Location added successfully!", "success");
            }
        } catch (error) {
            showToastNotification(error.response?.data?.detail || "Failed to add location", "error");
        } finally {
            setTempMarker(null);
            setIsAddingMode(false);
            setActiveInfoWindow(null);
        }
    }, [tempMarker, user, updateUser, showToastNotification]);

    // Update an existing location
    const updateLocation = useCallback(async (location) => {
        try {
            showToastNotification("Updating your location...", "info");

            const originalCoord = location.originalCoordinate || location.coordinate;
            const originalProx = location.originalProximity || location.proximity;

            const response = await axios.put(base_url + '/favorite-locations/update', {
                old_location: { coordinate: originalCoord, proximity: originalProx },
                new_location: { coordinate: location.coordinate, proximity: location.proximity }
            }, { params: { email: user.email } });

            if (response.data.success) {
                const locationsWithIds = response.data.fav_coordinates_and_proximity.map(loc => ({
                    ...loc,
                    id: loc.id || `loc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                }));

                setFavoriteLocations(locationsWithIds);
                setMunicipalityCount(response.data.favourite_municipalities.length);
                updateUser({
                    ...user,
                    fav_coordinates_and_proximity: response.data.fav_coordinates_and_proximity,
                    favourite_municipalities: response.data.favourite_municipalities
                });

                showToastNotification("Location updated successfully!", "success");
            }
        } catch (error) {
            showToastNotification(error.response?.data?.detail || "Failed to update location", "error");
        } finally {
            setActiveInfoWindow(null);
            setShowConfirmation(false);
            setPendingUpdate(null);
        }
    }, [user, updateUser, showToastNotification]);

    // Delete a location
    const deleteLocation = useCallback(async (location) => {
        try {
            showToastNotification("Deleting your location...", "info");

            const response = await axios.delete(base_url + '/favorite-locations', {
                data: {
                    coordinate: location.coordinate,
                    proximity: location.proximity
                },
                params: { email: user.email }
            });

            if (response.data.success) {
                const locationsWithIds = response.data.fav_coordinates_and_proximity.map(loc => ({
                    ...loc,
                    id: loc.id || `loc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                }));

                setFavoriteLocations(locationsWithIds);
                setMunicipalityCount(response.data.favourite_municipalities.length);
                updateUser({
                    ...user,
                    fav_coordinates_and_proximity: response.data.fav_coordinates_and_proximity,
                    favourite_municipalities: response.data.favourite_municipalities
                });

                showToastNotification("Location deleted successfully!", "success");
            }
        } catch (error) {
            showToastNotification(error.response?.data?.detail || "Failed to remove location", "error");
        } finally {
            setActiveInfoWindow(null);
        }
    }, [user, updateUser, showToastNotification]);

    // Updated drag handlers with extendable toasts
    const handleMarkerDragStart = useCallback(() => {
        setShowTooltip(true);
        if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
        }

        // Use extendable toast for drag start
        showToastNotification(
            "Drag the marker to your desired location",
            "info",
            "drag-marker", // unique key for this toast type
            true // make it extendable
        );
    }, [showToastNotification]);

    const handleMarkerDragEnd = useCallback((location, e) => {
        const newCoord = [e.latLng.lat(), e.latLng.lng()];
        setFavoriteLocations(prev => prev.map(loc =>
            loc.id === location.id
                ? { ...loc, coordinate: newCoord, originalCoordinate: loc.originalCoordinate || loc.coordinate }
                : loc
        ));

        setActiveInfoWindow({ ...location, coordinate: newCoord });

        // Use extendable toast for drag end
        showToastNotification(
            "Position updated! Click 'Save' to confirm",
            "info",
            "drag-complete", // unique key for this toast type
            true // make it extendable
        );

        // Hide tooltip after drag
        setShowTooltip(false);
        if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
        }
        tooltipTimeoutRef.current = setTimeout(() => {
            setShowTooltip(false);
        }, 3000);
    }, [showToastNotification]);

    // Updated temp marker drag handler
    const handleTempMarkerDragEnd = useCallback((e) => {
        const newCoord = [e.latLng.lat(), e.latLng.lng()];
        setTempMarker(prev => ({ ...prev, coordinate: newCoord }));
        setActiveInfoWindow(prev => ({ ...prev, coordinate: newCoord }));

        // Use extendable toast for temp marker drag
        showToastNotification(
            "Position updated! Use the radius slider to adjust area",
            "info",
            "temp-drag-complete", // unique key for this toast type
            true // make it extendable
        );

        // Hide tooltip after drag
        setShowTooltip(false);
    }, [showToastNotification]);

    // Handle tour navigation
    const handleTourNext = useCallback(() => {
        setTourStep(s => s + 1);
    }, []);

    const handleTourPrev = useCallback(() => {
        setTourStep(s => Math.max(0, s - 1));
    }, []);

    const startTour = useCallback(() => {
        setShowHelp(false)
        setShowTour(true);
        setTourStep(0);
        showToastNotification("Welcome to the guided tour! Follow along to learn how to use this map.", "info");
    }, [showToastNotification]);

    // InfoWindow content for existing locations with improved UI
    const renderLocationInfoWindow = (location) => (
        <div style={{
            minWidth: 250,
            padding: "12px 16px",
            borderRadius: 8,
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            background: "#fff",
            transition: "all 0.2s ease"
        }}>
            <div style={{
                fontWeight: "bold",
                marginBottom: 10,
                fontSize: 18,
                color: "#10c9c0",
                borderBottom: "2px solid #e0f7fa",
                paddingBottom: 8
            }}>
                Favorite Area
            </div>
            <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 6, fontWeight: 500 }}>Radius:</div>
                <input
                    className="radius-slider"
                    type="range"
                    min={1}
                    max={30}
                    value={location.proximity}
                    onChange={e =>
                        handleLocationRadiusChange(location.id, Number(e.target.value))
                    }
                    style={{
                        width: "100%",
                        accentColor: "#10c9c0",
                        height: 8,
                        borderRadius: 4
                    }}
                />
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 4
                }}>
                    <span style={{ color: "#666" }}>1km</span>
                    <span style={{
                        fontWeight: 600,
                        color: "#10c9c0"
                    }}>{location.proximity} km</span>
                    <span style={{ color: "#666" }}>30km</span>
                </div>
            </div>
            <div style={{
                marginTop: 16,
                display: "flex",
                gap: 8,
                justifyContent: "space-between"
            }}>
                <button
                    onClick={() => {
                        setPendingUpdate(location);
                        setShowConfirmation(true);
                    }}
                    style={{
                        background: "#10c9c0",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "8px 16px",
                        cursor: "pointer",
                        flex: 1,
                        fontWeight: 600,
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 5px rgba(16, 201, 192, 0.3)"
                    }}
                >
                    Save
                </button>
                <button
                    onClick={() => deleteLocation(location)}
                    style={{
                        background: "#fff",
                        color: "#ff6b6b",
                        border: "1px solid #ff6b6b",
                        borderRadius: 8,
                        padding: "8px 10px",
                        cursor: "pointer",
                        fontWeight: 600,
                        transition: "all 0.2s ease",
                    }}
                >
                    Delete
                </button>
            </div>
            <div style={{
                marginTop: 12,
                fontSize: 13,
                color: "#666",
                fontStyle: "italic",
                textAlign: "center"
            }}>
                Click and drag the marker to move
            </div>
        </div>
    );

    // InfoWindow content for temp marker with improved UI
    const renderTempInfoWindow = (marker) => (
        <div style={{
            minWidth: 250,
            padding: 16,
            borderRadius: 12,
            background: "#fff",
            boxShadow: "0 2px 12px rgba(0,0,0,0.13)",
            transition: "all 0.3s ease"
        }}>
            <div style={{
                fontWeight: "bold",
                marginBottom: 12,
                fontSize: 18,
                color: "#4285F4",
                borderBottom: "2px solid #e3f2fd",
                paddingBottom: 8
            }}>
                New Favorite Area
            </div>
            <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 6, fontWeight: 500 }}>Radius:</div>
                <input
                    className="radius-slider"
                    type="range"
                    min={1}
                    max={30}
                    value={marker.proximity}
                    onChange={e => handleTempRadiusChange(Number(e.target.value))}
                    style={{
                        width: "100%",
                        accentColor: "#4285F4",
                        background: "#e3f2fd",
                        borderRadius: 6,
                        height: 8
                    }}
                />
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 4
                }}>
                    <span style={{ color: "#666" }}>1km</span>
                    <span style={{
                        fontWeight: 600,
                        color: "#4285F4"
                    }}>{marker.proximity} km</span>
                    <span style={{ color: "#666" }}>30km</span>
                </div>
            </div>

            <div style={{
                display: "flex",
                gap: 8,
                marginTop: 16,
                justifyContent: "space-between"
            }}>
                <button
                    onClick={saveNewLocation}
                    style={{
                        background: "#4285F4",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "10px 16px",
                        fontWeight: 600,
                        fontSize: 16,
                        flex: 1,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 5px rgba(66, 133, 244, 0.3)"
                    }}
                >
                    Save Location
                </button>
                <button
                    onClick={() => {
                        setTempMarker(null);
                        setIsAddingMode(false);
                        setActiveInfoWindow(null);
                    }}
                    style={{
                        background: "#fff",
                        color: "#4285F4",
                        border: "1.5px solid #4285F4",
                        borderRadius: 8,
                        padding: "10px 12px",
                        fontWeight: 600,
                        fontSize: 16,
                        cursor: "pointer",
                        transition: "all 0.2s ease"
                    }}
                >
                    Cancel
                </button>
            </div>
            <div style={{
                marginTop: 12,
                fontSize: 13,
                color: "#666",
                fontStyle: "italic",
                textAlign: "center"
            }}>
                Click and drag the marker to move
            </div>
        </div>
    );

    if (loadError) return <div>Map cannot be loaded right now.</div>;
    if (!isLoaded) return <div>Loading...</div>;

    // --- Tour overlay with improved positioning and styling ---
    let tourOverlay = null;
    if (showTour && TOUR_STEPS[tourStep]) {
        const step = TOUR_STEPS[tourStep];
        const el = document.querySelector(step.selector);
        if (el) {
            const rect = el.getBoundingClientRect();
            const overlayWidth = 320;
            const overlayHeight = 120;
            let style = {
                position: "fixed",
                zIndex: 10000, // Extremely high z-index
                background: "#fff",
                color: "#1976d2",
                borderRadius: 12,
                padding: 20,
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                fontSize: 16,
                fontWeight: 500,
                minWidth: overlayWidth,
                maxWidth: overlayWidth,
                maxHeight: 300,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                border: "2px solid #10c9c0",
                transition: "all 0.3s ease",
                pointerEvents: "auto" // Ensure clicks work
            };

            // Default: right of element
            let left = rect.right + 20;
            let top = rect.top;
            let arrow = null;

            // Try to place based on step.placement, but flip if out of bounds
            if (step.placement === "right") {
                // If going off right edge, flip to left
                if (left + overlayWidth > window.innerWidth) {
                    left = rect.left - overlayWidth - 20;
                    style.left = `${Math.max(left, 10)}px`;
                    style.top = `${Math.max(top, 10)}px`;
                    arrow = <div style={{
                        position: "absolute",
                        right: -16,
                        top: 16,
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderLeft: "16px solid #10c9c0"
                    }} />;
                } else {
                    style.left = `${left}px`;
                    style.top = `${Math.max(top, 10)}px`;
                    arrow = <div style={{
                        position: "absolute",
                        left: -16,
                        top: 16,
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderRight: "16px solid #10c9c0"
                    }} />;
                }
            } else if (step.placement === "left") {
                let overlayLeft = rect.left - overlayWidth - 20;
                // If going off left edge, flip to right
                if (overlayLeft < 0) {
                    overlayLeft = rect.right + 20;
                    style.left = `${overlayLeft}px`;
                    style.top = `${Math.max(top, 10)}px`;
                    arrow = <div style={{
                        position: "absolute",
                        left: -16,
                        top: 16,
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderRight: "16px solid #10c9c0"
                    }} />;
                } else {
                    style.left = `${overlayLeft}px`;
                    style.top = `${Math.max(top, 10)}px`;
                    arrow = <div style={{
                        position: "absolute",
                        right: -16,
                        top: 16,
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderLeft: "16px solid #10c9c0"
                    }} />;
                }
            } else if (step.placement === "top") {
                let overlayTop = rect.top - overlayHeight - 20;
                // If going off top, place below
                if (overlayTop < 0) {
                    overlayTop = rect.bottom + 20;
                    style.left = `${rect.left}px`;
                    style.top = `${overlayTop}px`;
                    arrow = <div style={{
                        position: "absolute",
                        left: 30,
                        top: -16,
                        width: 0,
                        height: 0,
                        borderLeft: "8px solid transparent",
                        borderRight: "8px solid transparent",
                        borderBottom: "16px solid #10c9c0"
                    }} />;
                } else {
                    style.left = `${rect.left}px`;
                    style.top = `${overlayTop}px`;
                    arrow = <div style={{
                        position: "absolute",
                        left: 30,
                        bottom: -16,
                        width: 0,
                        height: 0,
                        borderLeft: "8px solid transparent",
                        borderRight: "8px solid transparent",
                        borderTop: "16px solid #10c9c0"
                    }} />;
                }
            } else {
                // Default to below
                let overlayTop = rect.bottom + 20;
                if (overlayTop + overlayHeight > window.innerHeight) {
                    overlayTop = rect.top - overlayHeight - 20;
                }
                style.left = `${rect.left}px`;
                style.top = `${overlayTop}px`;
                arrow = <div style={{
                    position: "absolute",
                    left: 30,
                    top: -16,
                    width: 0,
                    height: 0,
                    borderLeft: "8px solid transparent",
                    borderRight: "8px solid transparent",
                    borderBottom: "16px solid #10c9c0"
                }} />;
            }

            // Clamp to screen edges
            style.left = `${Math.max(10, Math.min(parseInt(style.left), window.innerWidth - overlayWidth - 10))}px`;
            style.top = `${Math.max(10, Math.min(parseInt(style.top), window.innerHeight - overlayHeight - 10))}px`;

            tourOverlay = (
                <div className="tour-overlay" style={{ ...style, position: "fixed" }}>
                    {arrow}
                    <div style={{ marginBottom: 16 }}>{step.message}</div>
                    <div style={{
                        marginTop: 16,
                        display: "flex",
                        gap: 12,
                        justifyContent: "space-between",
                        width: "100%"
                    }}>
                        <button
                            onClick={handleTourPrev}
                            disabled={tourStep === 0}
                            style={{
                                color: "#10c9c0",
                                background: tourStep === 0 ? "#f0f0f0" : "#e3f2fd",
                                border: "none",
                                borderRadius: 8,
                                padding: "8px 16px",
                                fontWeight: 600,
                                cursor: tourStep === 0 ? "default" : "pointer",
                                opacity: tourStep === 0 ? 0.5 : 1,
                                transition: "all 0.2s ease"
                            }}
                        >
                            ← Back
                        </button>
                        <button
                            onClick={handleTourNext}
                            style={{
                                color: "#fff",
                                background: "#10c9c0",
                                border: "none",
                                borderRadius: 8,
                                padding: "8px 24px",
                                fontWeight: 600,
                                cursor: "pointer",
                                boxShadow: "0 2px 8px rgba(16, 201, 192, 0.3)",
                                transition: "all 0.2s ease"
                            }}
                        >
                            {tourStep < TOUR_STEPS.length - 1 ? "Next →" : "Finish"}
                        </button>
                    </div>
                </div>
            );
        }
    }

    // --- Main render with improved UI ---
    return (
        <div style={{ position: 'relative', width: "100vw", height: "80vh" }}>
            {/* Add this directly before your closing div */}
            <ToastContainer
                position="top-right"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{
                    zIndex: 10001,
                    width: "auto",
                    maxWidth: "400px",
                    position: "fixed",
                    top: "50px !important",
                    right: "20px",
                    left: "auto" // This ensures it's on the right side
                }}
                containerId="toast-container-high-z"
            />
            {tourOverlay}

            <GoogleMap
                id="tour-map-area"
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={mapZoom}
                options={mapOptions}
                onClick={!showTour ? handleMapClick : undefined}
                onLoad={onMapLoad}
            >
                {favoriteLocations.map((location, idx) => (
                    <React.Fragment key={location.id}>
                        <LocationMarker
                            position={{ lat: location.coordinate[0], lng: location.coordinate[1] }}
                            draggable={true}
                            onClick={() => setActiveInfoWindow(location)}
                            onDragEnd={e => handleMarkerDragEnd(location, e)}
                            onDragStart={handleMarkerDragStart}
                            isTemp={false}
                        />
                        <Circle
                            center={{ lat: location.coordinate[0], lng: location.coordinate[1] }}
                            radius={location.proximity * 1000}
                            options={circleOptions}
                        />
                        {activeInfoWindow && activeInfoWindow.id === location.id && (
                            <InfoWindow
                                position={{ lat: location.coordinate[0], lng: location.coordinate[1] }}
                                onCloseClick={() => setActiveInfoWindow(null)}
                                options={{
                                    pixelOffset: new window.google.maps.Size(0, -30),
                                    maxWidth: 300,
                                    zIndex: 2000 // High z-index for InfoWindow
                                }}
                            >
                                {renderLocationInfoWindow(location)}
                            </InfoWindow>
                        )}
                    </React.Fragment>
                ))}

                {tempMarker && (
                    <>
                        <LocationMarker
                            position={{ lat: tempMarker.coordinate[0], lng: tempMarker.coordinate[1] }}
                            draggable={true}
                            onClick={() => setActiveInfoWindow(tempMarker)}
                            onDragEnd={handleTempMarkerDragEnd}
                            onDragStart={handleMarkerDragStart}
                            isTemp={true}
                        />
                        <Circle
                            center={{ lat: tempMarker.coordinate[0], lng: tempMarker.coordinate[1] }}
                            radius={tempMarker.proximity * 1000}
                            options={{
                                ...circleOptions,
                                fillColor: "#4285F4",
                                strokeColor: "#4285F4",
                            }}
                        />
                        {activeInfoWindow && activeInfoWindow.id === tempMarker.id && (
                            <InfoWindow
                                position={{ lat: tempMarker.coordinate[0], lng: tempMarker.coordinate[1] }}
                                onCloseClick={() => setActiveInfoWindow(null)}
                                options={{
                                    pixelOffset: new window.google.maps.Size(0, -30),
                                    maxWidth: 300,
                                    zIndex: 2000 // High z-index for InfoWindow 
                                }}
                            >
                                {renderTempInfoWindow(tempMarker)}
                            </InfoWindow>
                        )}
                    </>
                )}
            </GoogleMap>

            {/* Drag tooltip */}
            {showTooltip && (
                <div className=''>
                    <div style={{
                        position: "fixed",
                        top: "83%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "rgba(0,0,0,0.7)",
                        color: "#fff",
                        padding: "12px 20px",
                        borderRadius: 30,
                        zIndex: 3000, // Ultra high z-index
                        pointerEvents: "none",
                        fontWeight: 500,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                        animation: "fadeInOut 2s ease-in-out"
                    }}>
                        Drag to move the pin
                    </div>

                    <div style={{
                        position: "fixed",
                        top: "88%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "rgba(0,0,0,0.7)",
                        color: "#fff",
                        padding: "12px 20px",
                        borderRadius: 30,
                        zIndex: 3000, // Ultra high z-index
                        pointerEvents: "none",
                        fontWeight: 500,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                        animation: "fadeInOut 2s ease-in-out"
                    }}>
                        Click on the pin to update it
                    </div>
                </div>
            )}

            {/* UI Controls with improved styling */}
            <div style={{
                position: "absolute",
                top: 24,
                left: 24,
                zIndex: 1000, // Increased z-index to always be on top
                display: "flex",
                flexDirection: "column",
                gap: 16,
                transition: "all 0.3s ease"
            }}>
                <div id="zone-counter" style={{
                    background: "#10c9c0",
                    color: "#fff",
                    borderRadius: 12,
                    padding: "12px 24px",
                    fontWeight: 700,
                    fontSize: 18,
                    boxShadow: "0 4px 12px rgba(16, 201, 192, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    transition: "all 0.3s ease",
                    transform: showTour && TOUR_STEPS[tourStep]?.selector === "#zone-counter" ? "scale(1.05)" : "scale(1)"
                }}>
                    <span style={{ marginRight: 8 }}>📍</span>
                    Zones: {municipalityCount}
                </div>

                <button
                    id="add-location-btn"
                    onClick={startAddingLocation}
                    style={{
                        background: "#10c9c0",
                        color: "#fff",
                        border: "none",
                        borderRadius: 12,
                        padding: "12px 24px",
                        fontWeight: 700,
                        fontSize: 18,
                        cursor: "pointer",
                        opacity: favoriteLocations.length >= 3 ? 0.6 : 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        boxShadow: "0 4px 12px rgba(16, 201, 192, 0.3)",
                        transition: "all 0.3s ease",
                        transform: showTour && TOUR_STEPS[tourStep]?.selector === "#add-location-btn" ? "scale(1.05)" : "scale(1)"
                    }}
                >
                    <span>➕</span> Add New Location
                </button>

                <button
                    id="help-btn"
                    onClick={() => setShowHelp(true)}
                    style={{
                        background: "#fff",
                        color: "#10c9c0",
                        border: "2px solid #10c9c0",
                        borderRadius: 12,
                        padding: "12px 24px",
                        fontWeight: 700,
                        fontSize: 18,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
                        transition: "all 0.3s ease",
                        transform: showTour && TOUR_STEPS[tourStep]?.selector === "#help-btn" ? "scale(1.05)" : "scale(1)"
                    }}
                >
                    <span>❓</span> Help
                </button>

                <button
                    id="tour-btn"
                    onClick={startTour}
                    style={{
                        background: "#fff",
                        color: "#1976d2",
                        border: "2px solid #1976d2",
                        borderRadius: 12,
                        padding: "12px 24px",
                        fontWeight: 700,
                        fontSize: 18,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
                        transition: "all 0.3s ease",
                        transform: showTour && TOUR_STEPS[tourStep]?.selector === "#tour-btn" ? "scale(1.05)" : "scale(1)"
                    }}
                >
                    <span>🔍</span> Start Guided Tour
                </button>

                <button
                    onClick={goToSettings}
                    id="add-location-btn"
                    style={{
                        background: "#10c9c0",
                        color: "#fff",
                        border: "none",
                        borderRadius: 12,
                        padding: "12px 24px",
                        fontWeight: 700,
                        fontSize: 18,
                        cursor: "pointer",
                        opacity: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        boxShadow: "0 4px 12px rgba(16, 201, 192, 0.3)",
                        transition: "all 0.3s ease",
                        transform: showTour && TOUR_STEPS[tourStep]?.selector === "#add-location-btn" ? "scale(1.05)" : "scale(1)"
                    }}
                >
                    <span>➕</span> Filter your listings!
                </button>
            </div>

            {/* Confirmation Modal with improved styling */}
            {showConfirmation && (
                <div className="modal-overlay" style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(0,0,0,0.4)",
                    zIndex: 3000, // Ultra high z-index
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(3px)",
                    animation: "fadeIn 0.3s ease",
                    pointerEvents: "auto" // Ensure clicks work
                }}>
                    <div className="modal-content" style={{
                        background: "#fff",
                        borderRadius: 16,
                        padding: 32,
                        minWidth: 360,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                        animation: "scaleIn 0.3s ease",
                        border: "2px solid #10c9c0"
                    }}>
                        <div style={{
                            fontWeight: 700,
                            fontSize: 22,
                            marginBottom: 24,
                            color: "#10c9c0",
                            textAlign: "center",
                            borderBottom: "2px solid #e0f7fa",
                            paddingBottom: 12
                        }}>
                            Save changes?
                        </div>
                        <div style={{
                            textAlign: "center",
                            marginBottom: 24,
                            fontSize: 16,
                            color: "#444"
                        }}>
                            Are you sure you want to update this location?
                        </div>
                        <div style={{
                            display: "flex",
                            gap: 16,
                            justifyContent: "center"
                        }}>
                            <button
                                onClick={() => {
                                    if (pendingUpdate) updateLocation(pendingUpdate);
                                }}
                                style={{
                                    background: "#10c9c0",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 12,
                                    padding: "12px 28px",
                                    fontWeight: 600,
                                    fontSize: 16,
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 4px 10px rgba(16, 201, 192, 0.3)"
                                }}
                            >
                                Save
                            </button>
                            <button
                                onClick={() => {
                                    setShowConfirmation(false);
                                    setPendingUpdate(null);
                                }}
                                style={{
                                    background: "#fff",
                                    color: "#666",
                                    border: "2px solid #ddd",
                                    borderRadius: 12,
                                    padding: "12px 28px",
                                    fontWeight: 600,
                                    fontSize: 16,
                                    cursor: "pointer",
                                    transition: "all 0.2s ease"
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Help Modal with improved content and styling */}
            {showHelp && (
                <div className="modal-overlay" style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(0,0,0,0.4)",
                    zIndex: 3000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(3px)",
                    animation: "fadeIn 0.3s ease",
                    pointerEvents: "auto"
                }}>
                    <div className="modal-content" style={{
                        background: "#fff",
                        borderRadius: 16,
                        padding: 32,
                        maxWidth: 600,
                        width: "90%",
                        maxHeight: "85vh",
                        overflowY: "auto",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                        animation: "scaleIn 0.3s ease",
                        border: "2px solid #10c9c0"
                    }}>
                        <div style={{
                            fontWeight: 700,
                            fontSize: 24,
                            marginBottom: 24,
                            color: "#10c9c0",
                            textAlign: "center",
                            borderBottom: "2px solid #e0f7fa",
                            paddingBottom: 12
                        }}>
                            How It Works
                        </div>

                        {/* Step-by-step process */}
                        <div style={{ marginBottom: 28 }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 20,
                                padding: 16,
                                background: "#f0fdfc",
                                borderRadius: 12,
                                border: "1px solid #b2f5f0"
                            }}>
                                <div style={{
                                    background: "#10c9c0",
                                    color: "white",
                                    borderRadius: "50%",
                                    width: 32,
                                    height: 32,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    fontSize: 16,
                                    marginRight: 16,
                                    flexShrink: 0
                                }}>
                                    1
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 16, color: "#333", marginBottom: 4 }}>
                                        Add Your Favorite Locations
                                    </div>
                                    <div style={{ fontSize: 14, color: "#666", lineHeight: 1.5 }}>
                                        Click "Add New Location" to place markers on areas you're interested in. You can add up to 3 favorite locations.
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 20,
                                padding: 16,
                                background: "#f0fdfc",
                                borderRadius: 12,
                                border: "1px solid #b2f5f0"
                            }}>
                                <div style={{
                                    background: "#10c9c0",
                                    color: "white",
                                    borderRadius: "50%",
                                    width: 32,
                                    height: 32,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    fontSize: 16,
                                    marginRight: 16,
                                    flexShrink: 0
                                }}>
                                    2
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 16, color: "#333", marginBottom: 4 }}>
                                        Set Your Search Radius
                                    </div>
                                    <div style={{ fontSize: 14, color: "#666", lineHeight: 1.5 }}>
                                        Adjust the radius (1-30km) around each marker to define your search area. Larger areas = more potential listings.
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 20,
                                padding: 16,
                                background: "#f0fdfc",
                                borderRadius: 12,
                                border: "1px solid #b2f5f0"
                            }}>
                                <div style={{
                                    background: "#10c9c0",
                                    color: "white",
                                    borderRadius: "50%",
                                    width: 32,
                                    height: 32,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    fontSize: 16,
                                    marginRight: 16,
                                    flexShrink: 0
                                }}>
                                    3
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 16, color: "#333", marginBottom: 4 }}>
                                        Receive Daily Email Updates
                                    </div>
                                    <div style={{ fontSize: 14, color: "#666", lineHeight: 1.5 }}>
                                        New property listings will be sent directly to your registered email address every day when available.
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Map Controls */}
                        <div style={{ marginBottom: 28 }}>
                            <h3 style={{
                                color: "#10c9c0",
                                fontWeight: 600,
                                fontSize: 18,
                                marginBottom: 16,
                                display: "flex",
                                alignItems: "center"
                            }}>
                                <span style={{
                                    background: "#10c9c0",
                                    width: 4,
                                    height: 20,
                                    marginRight: 12,
                                    borderRadius: 2
                                }}></span>
                                Map Controls
                            </h3>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 12,
                                fontSize: 14
                            }}>
                                <div style={{
                                    padding: 12,
                                    background: "#fafafa",
                                    borderRadius: 8,
                                    border: "1px solid #e0e0e0"
                                }}>
                                    <strong style={{ color: "#333" }}>Blue Marker:</strong>
                                    <div style={{ color: "#666", marginTop: 4 }}>New location being added</div>
                                </div>
                                <div style={{
                                    padding: 12,
                                    background: "#fafafa",
                                    borderRadius: 8,
                                    border: "1px solid #e0e0e0"
                                }}>
                                    <strong style={{ color: "#333" }}>Green Marker:</strong>
                                    <div style={{ color: "#666", marginTop: 4 }}>Saved favorite locations</div>
                                </div>
                                <div style={{
                                    padding: 12,
                                    background: "#fafafa",
                                    borderRadius: 8,
                                    border: "1px solid #e0e0e0"
                                }}>
                                    <strong style={{ color: "#333" }}>Drag & Drop:</strong>
                                    <div style={{ color: "#666", marginTop: 4 }}>Click and hold to move markers</div>
                                </div>
                                <div style={{
                                    padding: 12,
                                    background: "#fafafa",
                                    borderRadius: 8,
                                    border: "1px solid #e0e0e0"
                                }}>
                                    <strong style={{ color: "#333" }}>Save Changes:</strong>
                                    <div style={{ color: "#666", marginTop: 4 }}>Always click "Save" to confirm</div>
                                </div>
                            </div>
                        </div>

                        {/* Email Notifications */}
                        <div style={{ marginBottom: 28 }}>
                            <h3 style={{
                                color: "#10c9c0",
                                fontWeight: 600,
                                fontSize: 18,
                                marginBottom: 16,
                                display: "flex",
                                alignItems: "center"
                            }}>
                                <span style={{
                                    background: "#10c9c0",
                                    width: 4,
                                    height: 20,
                                    marginRight: 12,
                                    borderRadius: 2
                                }}></span>
                                Email Notifications
                            </h3>
                            <div style={{
                                padding: 16,
                                background: "#fff7ed",
                                borderRadius: 12,
                                border: "1px solid #fed7aa"
                            }}>
                                <div style={{ fontSize: 14, color: "#333", lineHeight: 1.6 }}>
                                    <div style={{ marginBottom: 8 }}>
                                        <strong>📧 Daily Updates:</strong> You'll receive property listings via the email address you used to log in.
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <strong>📅 Frequency:</strong> Emails are sent daily, but only when new listings are available in your areas.
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <strong>🎯 No Listings?</strong> If you don't receive an email, it means no new properties were listed that day - but check again tomorrow!
                                    </div>
                                    <div>
                                        <strong>📊 Pro Tip:</strong> Areas with higher zone numbers have better chances of frequent new listings.
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: 32,
                            gap: 16
                        }}>
                            <button
                                onClick={startTour}
                                style={{
                                    background: "#fff",
                                    color: "#1976d2",
                                    border: "2px solid #1976d2",
                                    borderRadius: 12,
                                    padding: "12px 20px",
                                    fontWeight: 600,
                                    fontSize: 16,
                                    cursor: "pointer",
                                    flex: 1,
                                    transition: "all 0.2s ease"
                                }}
                            >
                                Start Tour
                            </button>
                            <button
                                onClick={() => setShowHelp(false)}
                                style={{
                                    background: "#10c9c0",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 12,
                                    padding: "12px 20px",
                                    fontWeight: 600,
                                    fontSize: 16,
                                    cursor: "pointer",
                                    flex: 1,
                                    boxShadow: "0 4px 10px rgba(16, 201, 192, 0.3)",
                                    transition: "all 0.2s ease"
                                }}
                            >
                                Got It!
                            </button>
                        </div>
                    </div>
                </div>            )}

            {/* Toast container with high z-index */}
            <div id="toast-container-wrapper" style={{
                position: "fixed",
                top: 20,
                right: 20,
                zIndex: 10000, // Ultra high z-index for toasts
                pointerEvents: "none" // Allow clicks to pass through
            }}></div>

            {/* CSS for animations, tour highlight and UI improvements */}
            <style>{`
            /* Toast container styles to ensure visibility */
            .Toastify__toast-container {
                z-index: 10000 !important;
                position: relative;
            }
            
            .Toastify__toast-container--top-right {
        top: 180px !important;
        right: 20px !important;
        left: auto !important;
        position: fixed !important;
    }
    
    /* High z-index for visibility */
    .Toastify__toast-container {
        z-index: 10001 !important;
        position: fixed !important;
    }
    
    .Toastify__toast {
        z-index: 10001 !important;
        position: relative !important;
    }
    
    /* Add this to fix any positioning issues */
    #toast-container-high-z {
        position: fixed !important;
        top: 20px !important;
        right: 20px !important;
        left: auto !important;
        z-index: 10001 !important;
    }

            .Toastify__toast {
                z-index: 10000 !important;
                position: relative;
            }
            
            /* Google Maps InfoWindow fixes */
            .gm-style-iw,
            .gm-style-iw-a,
            .gm-style-iw-t,
            .gm-style-iw-c,
            .gm-style-iw-d {
                z-index: 2000 !important;
            }
            
            /* Fix for any parent containers */
            .gm-style-iw-a > div,
            .gm-style-iw-t > div,
            .gm-style > div {
                z-index: 2000 !important;
            }
            
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            
            @keyframes scaleIn {
                from { transform: scale(0.9); opacity: 0; }
                to { transform: scale(1); opacity: 1; }
            }
            
            @keyframes fadeInOut {
                0% { opacity: 0; }
                20% { opacity: 1; }
                80% { opacity: 1; }
                100% { opacity: 0; }
            }
            
            @keyframes pulse {
                0% { transform: scale(1); }
                50% { transform: scale(1.05); }
                100% { transform: scale(1); }
            }
            
            .tour-highlight {
                position: relative !important;
                z-index: 10000 !important;
                box-shadow: 0 0 0 4px #10c9c0, 0 0 0 8px rgba(16, 201, 192, 0.3) !important;
                animation: pulse 2s infinite ease-in-out !important;
                pointer-events: auto !important;
            }
            
            .tour-overlay {
                animation: scaleIn 0.3s ease;
            }
            
            .modal-overlay {
                animation: fadeIn 0.3s ease;
            }
            
            .modal-content {
                animation: scaleIn 0.3s ease;
            }
            
            /* Improved slider styling */
            input[type="range"] {
                -webkit-appearance: none;
                height: 8px;
                border-radius: 4px;
                background: #e0f7fa;
                outline: none;
                transition: all 0.3s ease;
            }
            
            input[type="range"]::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: #10c9c0;
                cursor: pointer;
                box-shadow: 0 1px 5px rgba(0,0,0,0.2);
                transition: all 0.2s ease;
            }
            
            input[type="range"]::-webkit-slider-thumb:hover {
                transform: scale(1.1);
                background: #0eb8b0;
            }
            
            /* Button transitions */
            button {
                transition: all 0.2s ease !important;
            }
            
            button:hover:not(:disabled) {
                transform: translateY(-2px) !important;
                box-shadow: 0 6px 14px rgba(0,0,0,0.1) !important;
            }
            
            button:active:not(:disabled) {
                transform: translateY(1px) !important;
            }
        `}</style>
        </div>
    );
};

export default FavoriteLocationsPage;